export enum RegistryErrorCode {
  IncorrectEntryArguments = 'IncorrectEntryArguments',
  IncorrectLibraryParams = 'IncorrectLibraryParams',
  IncorrectLoaderArguments = 'IncorrectLoaderArguments',
  IncorrectBundleExport = 'IncorrectBundleExport',
  IncorrectBatches = 'IncorrectBatches',
  FetchError = 'FetchError',
  BundleEvaluationError = 'BundleEvaluationError',
  InternalException = 'InternalException',
}

/**
 * NOTE: all error methods should be defined as the arrow functions
 * because of typescript issue when target is set `es5`
 */
export class RegistryError extends Error {
  public code?: RegistryErrorCode;

  public parent?: RegistryError;

  private hints: Array<string> = [];
  private messages: Array<{
    message: string;
    code?: RegistryErrorCode;
  }> = [];

  constructor(message: string, code?: RegistryErrorCode) {
    super(message);

    this.name = this.constructor.name;
    this.code = code;

    this.messages.push({
      message,
      code,
    });

    Object.defineProperty(this, 'message', {
      get: () => {
        const [header, ...rows] = this.messages;

        const prefix = header.code ? `${header.code}. ` : '';
        const title = `RegistryException – ${prefix}${header.message}`;
        const messages = [title];

        rows.forEach(row => {
          messages.push(`  – ${row.message}`);
        });

        if (this.hints.length) {
          messages.push('');
          messages.push(`Hints:`);

          messages.push(this.hints.map(m => `  – ${m}`).join('\n'));
        }

        if (this.parent) {
          messages.push('');
          messages.push(`Parent Error:`);
          messages.push(
            this.parent.message
              .split('\n')
              .map(m => `  ${m}`)
              .join('\n'),
          );
        }

        return messages.join('\n');
      },
    });

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RegistryError);
    }
  }

  withParentError = (error: RegistryError) => {
    this.parent = error;
    return this;
  };

  withDetails = (message: string, code?: RegistryErrorCode) => {
    this.messages.push({
      message,
      code,
    });

    return this;
  };

  withLibrary = (library: string) => {
    return this.withDetails(`Library: ${library}`);
  };

  withUrl = (url: string) => {
    return this.withDetails(`URL: ${url}`);
  };

  withRuntime = (runtime?: string) => {
    if (runtime) {
      this.withDetails(`Runtime: ${runtime}`);
    }

    return this;
  };

  withComponent = (component: string, part?: string, parts?: Array<string>) => {
    this.withDetails(`Component: ${component}`);

    if (part) {
      this.withDetails(`Component Part: ${part}`);
    }

    if (parts && parts?.length) {
      this.withDetails(`Available Parts: ${parts.join(', ')}`);
    }

    return this;
  };

  withMissMatch = (expected: string, received: string) => {
    return this.withDetails(`Expected value: ${expected}`).withDetails(
      `Received value: ${received}`,
    );
  };

  withOriginalErrorMessage = (message: string) => {
    return this.withDetails(`Original Error Message: ${message}`);
  };

  withHint = (hint: string) => {
    this.hints.push(hint);

    return this;
  };
}

export const createException = (message: string, code?: RegistryErrorCode) => {
  const e = new RegistryError(message, code);

  if (Error.captureStackTrace) {
    Error.captureStackTrace(e, createException);
  }

  return e;
};

export const createInternalException = (
  message: string = 'Registry internal exception or unreachable code.',
) => {
  const e = new RegistryError(message, RegistryErrorCode.InternalException);

  if (Error.captureStackTrace) {
    Error.captureStackTrace(e, createInternalException);
  }

  return e;
};
